import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import LaisWhatsappGovernanceAPI from 'dashboard/api/laisWhatsappGovernance';

const defaultWhatsappGovernance = () => ({
  checklist: {},
  metadata: {
    provider: 'embedded_signup',
    business_manager_owner: '',
    meta_portfolio_url: '',
    meta_terms_evidence: '',
    meta_ads_connected: false,
  },
  onboarding_status: 'not_started',
  display_name_status: 'none',
  meta_terms_accepted: false,
  profile_photo: { attached: false },
  description: '',
  about: '',
  address: '',
  contact_email: '',
  website_url: '',
  connection_id: null,
});

const normalizeWhatsappChecklist = (checklist = {}, items = []) =>
  items.reduce((acc, item) => {
    const current = checklist[item.key] || {};
    acc[item.key] = {
      checked: Boolean(current.checked),
      evidence: current.evidence || '',
      responsible: current.responsible || '',
      blocked: Boolean(current.blocked),
    };
    return acc;
  }, {});

/**
 * Meta onboarding checklist and WhatsApp business profile governance:
 * loading, saving, profile photo upload and display-name requests.
 */
export function useMinhaLaisWhatsappGovernance() {
  const isLoadingWhatsappGovernance = ref(false);
  const isSavingWhatsappOnboarding = ref(false);
  const isSavingWhatsappProfile = ref(false);
  const isUploadingWhatsappPhoto = ref(false);
  const isRequestingWhatsappDisplayName = ref(false);
  const whatsappChecklistItems = ref([]);
  const whatsappPhotoFile = ref(null);
  const whatsappRequestedDisplayName = ref('');
  const whatsappGovernance = ref({
    checklist: {},
    metadata: {},
    onboarding_status: 'not_started',
    display_name_status: 'none',
    meta_terms_accepted: false,
    profile_photo: { attached: false },
  });

  const applyWhatsappGovernancePayload = payload => {
    const data = payload?.data || {};
    const items = payload?.checklist_items || whatsappChecklistItems.value;
    whatsappChecklistItems.value = items;
    whatsappGovernance.value = {
      ...defaultWhatsappGovernance(),
      ...data,
      checklist: normalizeWhatsappChecklist(data.checklist || {}, items),
      metadata: {
        ...defaultWhatsappGovernance().metadata,
        ...(data.metadata || {}),
      },
    };
    whatsappRequestedDisplayName.value =
      whatsappGovernance.value.requested_display_name || '';
  };

  const carregarWhatsappGovernance = async ({ view = 'profile' } = {}) => {
    isLoadingWhatsappGovernance.value = true;
    try {
      const request =
        view === 'onboarding'
          ? LaisWhatsappGovernanceAPI.getOnboarding()
          : LaisWhatsappGovernanceAPI.getProfile();
      const { data } = await request;
      applyWhatsappGovernancePayload(data);
      return true;
    } catch (error) {
      useAlert(
        error.response?.data?.error ||
          'Não foi possível carregar a governança de WhatsApp.'
      );
      return false;
    } finally {
      isLoadingWhatsappGovernance.value = false;
    }
  };

  const salvarWhatsappOnboarding = async () => {
    isSavingWhatsappOnboarding.value = true;
    try {
      const { data } = await LaisWhatsappGovernanceAPI.updateOnboarding({
        checklist: whatsappGovernance.value.checklist,
        metadata: whatsappGovernance.value.metadata,
        meta_terms_accepted: whatsappGovernance.value.meta_terms_accepted,
      });
      applyWhatsappGovernancePayload(data);
      useAlert('Checklist Meta atualizado.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.join(', ') ||
          error.response?.data?.error ||
          'Não foi possível salvar o onboarding Meta.'
      );
    } finally {
      isSavingWhatsappOnboarding.value = false;
    }
  };

  const salvarWhatsappProfile = async () => {
    isSavingWhatsappProfile.value = true;
    try {
      const { data } = await LaisWhatsappGovernanceAPI.updateProfile({
        connection_id: whatsappGovernance.value.connection_id,
        description: whatsappGovernance.value.description,
        about: whatsappGovernance.value.about,
        address: whatsappGovernance.value.address,
        contact_email: whatsappGovernance.value.contact_email,
      });
      applyWhatsappGovernancePayload(data);
      useAlert('Perfil do WhatsApp atualizado.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.join(', ') ||
          error.response?.data?.error ||
          'Não foi possível salvar o perfil do WhatsApp.'
      );
    } finally {
      isSavingWhatsappProfile.value = false;
    }
  };

  const selecionarWhatsappPhoto = event => {
    const [file] = event.target.files || [];
    whatsappPhotoFile.value = file || null;
  };

  const enviarWhatsappProfilePhoto = async () => {
    if (!whatsappPhotoFile.value) {
      useAlert('Selecione uma imagem PNG ou JPG.');
      return;
    }

    isUploadingWhatsappPhoto.value = true;
    try {
      const formData = new FormData();
      formData.append('profile_photo', whatsappPhotoFile.value);
      const { data } = await LaisWhatsappGovernanceAPI.uploadPhoto(formData);
      applyWhatsappGovernancePayload(data);
      whatsappPhotoFile.value = null;
      useAlert('Foto do perfil enviada.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.join(', ') ||
          error.response?.data?.error ||
          'Não foi possível enviar a foto do perfil.'
      );
    } finally {
      isUploadingWhatsappPhoto.value = false;
    }
  };

  const solicitarWhatsappDisplayName = async () => {
    if (!whatsappRequestedDisplayName.value?.trim()) {
      useAlert('Informe o nome de exibição desejado.');
      return;
    }

    isRequestingWhatsappDisplayName.value = true;
    try {
      const { data } = await LaisWhatsappGovernanceAPI.requestDisplayName({
        requested_name: whatsappRequestedDisplayName.value,
      });
      applyWhatsappGovernancePayload(data);
      useAlert('Solicitação enviada para análise Meta.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.join(', ') ||
          error.response?.data?.error ||
          'Não foi possível solicitar a mudança de nome.'
      );
    } finally {
      isRequestingWhatsappDisplayName.value = false;
    }
  };

  const whatsappChecklistProgress = computed(() => {
    const requiredItems = whatsappChecklistItems.value.filter(
      item => item.required
    );
    const completed = requiredItems.filter(
      item => whatsappGovernance.value.checklist?.[item.key]?.checked
    ).length;
    return {
      completed,
      total: requiredItems.length,
    };
  });

  return {
    whatsappGovernance,
    whatsappChecklistItems,
    whatsappChecklistProgress,
    whatsappPhotoFile,
    whatsappRequestedDisplayName,
    isLoadingWhatsappGovernance,
    isSavingWhatsappOnboarding,
    isSavingWhatsappProfile,
    isUploadingWhatsappPhoto,
    isRequestingWhatsappDisplayName,
    carregarWhatsappGovernance,
    salvarWhatsappOnboarding,
    salvarWhatsappProfile,
    selecionarWhatsappPhoto,
    enviarWhatsappProfilePhoto,
    solicitarWhatsappDisplayName,
  };
}
